import React from 'react';
import { css } from 'aphrodite';
import { IconButton } from '@mui/material';
import { MoreVert } from '@mui/icons-material';

import Card from '~/src/components/Card';
import Table from '~/src/containers/Table';
import Dropdown from '~/src/components/Dropdown';
import { ReactComponent as DownloadIcon } from '~/src/static/download-icon.svg';
import dropdownStyles from '~/src/components/Dropdown/styles';

const BILLING_COLUMN_KEYS = {
  CREATED: 'created',
  AMOUNT_CAPTURED: 'amount_captured',
  RECEIPT_URL: 'receipt_url',
};

const InvoiceRowMenu = ({ invoice, onViewCreditNotes }) => {
  const hasCreditNotes = invoice.has_credit_notes;

  const handleViewCreditNotes = (event) => {
    event.stopPropagation();
    if (!hasCreditNotes) return;
    onViewCreditNotes(invoice);
  };

  return (
    <Dropdown
      renderTrigger={({ toggleDropdown }) => (
        <IconButton
          aria-label="Invoice actions"
          sx={{ fontSize: 16 }}
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown();
          }}
        >
          <MoreVert fontSize="inherit" />
        </IconButton>
      )}
    >
      <a
        href={invoice.receipt_url}
        target="_blank"
        rel="noopener noreferrer"
        className={css(dropdownStyles.dropdownActionItem)}
      >
        View PDF
      </a>
      <a
        onClick={handleViewCreditNotes}
        aria-disabled={!hasCreditNotes}
        className={css(
          dropdownStyles.dropdownActionItem,
          !hasCreditNotes && dropdownStyles.dropdownActionItemDisabled,
        )}
      >
        View credit notes
      </a>
    </Dropdown>
  );
};

const Pagination = ({ pagination, isLoading = false, testIdPrefix = '' }) => {
  if (!pagination) return null;

  const { currentPage, totalPages, onPageChange } = pagination;
  const testId = (id) => (testIdPrefix ? `${testIdPrefix}-${id}` : id);

  return (
    <div
      className="flex items-center justify-center mt-4 mb-4"
      data-testid={testId('pagination')}
    >
      <button
        className="px-3 py-1 mx-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isLoading || currentPage <= 1}
        data-testid={testId('prev-button')}
      >
        Previous
      </button>
      <div className="mx-2" data-testid={testId('page-info')}>
        Page {currentPage} of {totalPages}
      </div>
      <button
        className="px-3 py-1 mx-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isLoading || currentPage >= totalPages}
        data-testid={testId('next-button')}
      >
        Next
      </button>
    </div>
  );
};

const SettingsBilling = ({
  receipts,
  pagination,
  showInvoiceActions,
  isFetchingInvoices,
  selectedInvoice,
  invoiceCreditNotes,
  creditNotesPagination,
  isFetchingCreditNotes,
  hasCreditNotesError,
  onViewCreditNotes,
  onBack,
}) => {
  const renderHeader = () => 'Billing history';

  const invoiceColumns = [
    {
      dataKey: BILLING_COLUMN_KEYS.CREATED,
      style: { width: '40%', textAlign: 'left' },
    },
    {
      dataKey: BILLING_COLUMN_KEYS.AMOUNT_CAPTURED,
      style: { width: '50%', textAlign: 'left' },
    },
    {
      dataKey: BILLING_COLUMN_KEYS.RECEIPT_URL,
      render: (url, { rowData }) => {
        if (showInvoiceActions) {
          return (
            <InvoiceRowMenu
              invoice={rowData}
              onViewCreditNotes={onViewCreditNotes}
            />
          );
        }
        return (
          <a
            href={url}
            className="cursor-pointer bg-gray-100 rounded p-2 flex items-center justify-center"
          >
            <DownloadIcon />
          </a>
        );
      },
    },
  ];

  const creditNoteColumns = [
    {
      dataKey: BILLING_COLUMN_KEYS.CREATED,
      style: { width: '40%', textAlign: 'left' },
    },
    {
      dataKey: BILLING_COLUMN_KEYS.AMOUNT_CAPTURED,
      style: { width: '50%', textAlign: 'left' },
    },
    {
      dataKey: BILLING_COLUMN_KEYS.RECEIPT_URL,
      render: (url) => (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer bg-gray-100 rounded p-2 flex items-center justify-center"
        >
          <DownloadIcon />
        </a>
      ),
    },
  ];

  if (selectedInvoice) {
    return (
      <Card noPadding renderHeader={renderHeader}>
        <div className="flex items-center gap-3 px-4 pt-4 pb-2 border-b border-gray-200">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to billing history"
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            &#8592; Back
          </button>
          <span className="text-sm font-medium text-gray-700">
            Credit notes for invoice {selectedInvoice.created} &middot;{' '}
            {selectedInvoice.amount_captured}
          </span>
        </div>
        {isFetchingCreditNotes ? (
          <div className="px-4 py-6 text-sm text-gray-500">Loading...</div>
        ) : hasCreditNotesError ? (
          <div className="px-4 py-6 text-sm text-red-600">
            Unable to load credit notes. Please try again.
          </div>
        ) : (
          <>
            <Table
              emptyTitle={'No credit notes for this invoice'}
              noBorder={true}
              hideHeader={true}
              hideColumnHeaders
              data={invoiceCreditNotes}
              columns={creditNoteColumns}
            />
            <Pagination
              pagination={creditNotesPagination}
              isLoading={isFetchingCreditNotes}
              testIdPrefix="credit-notes"
            />
          </>
        )}
      </Card>
    );
  }

  return (
    <Card noPadding renderHeader={renderHeader}>
      <Table
        emptyTitle={'No billing history'}
        noBorder={true}
        hideHeader={true}
        data={receipts}
        hideColumnHeaders
        columns={invoiceColumns}
      />
      <Pagination pagination={pagination} isLoading={isFetchingInvoices} />
    </Card>
  );
};

SettingsBilling.defaultProps = {
  receipts: [],
  pagination: null,
  showInvoiceActions: false,
  isFetchingInvoices: false,
  selectedInvoice: null,
  invoiceCreditNotes: [],
  creditNotesPagination: null,
  isFetchingCreditNotes: false,
  hasCreditNotesError: false,
  onViewCreditNotes: () => {},
  onBack: () => {},
};

export default SettingsBilling;
